import sortedUniq from 'lodash/sortedUniq'
import { useQuery, useQueryClient, QueryClient, UseQueryOptions } from 'react-query'
import { useVvsMinerMoleContract, useVvsFarmBoostContract } from 'hooks/useContract'
import {
  queryKeyBoostedFarmUserInfos,
  queryBoostedFarmUserInfos,
  boostedFarmIdToUserInfosMap,
} from 'hooks/boost-farm/useBoostedFarmUserInfos'
import { QueryKey } from 'config/constants/queryKey'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { VvsMinerMole, VvsFarmBoost } from '../../../../types/ethers-contracts'

import { queryKeyTokensOfUser, queryTokensOfUser, useTokensOfUser } from './useTokensOfUser'
import { useFetchMyMoles } from './infoApi'

export const useMyMoles = () => {
  const {
    data: { tokenIds: molesIds } = {
      tokenIds: [],
    },
    isLoading: isTokensOfUserLoading,
  } = useTokensOfUser()
  const [myMoles, isMolesFetching] = useFetchMyMoles(molesIds)

  return {
    myMoles,
    molesIds,
    isLoading: isTokensOfUserLoading || isMolesFetching,
  }
}

export const queryKeyMyMoleIdsWithStaked = (account: string) => [QueryKey.MY_MOLE_IDS_WITH_STAKED, account]
export const queryMyMoleIdsWithStaked = async (
  queryClient: QueryClient,
  vvsMinerMolecontract: VvsMinerMole,
  vvsFarmBoostContract: VvsFarmBoost,
  account: string,
) => {
  if (!account) return { boostedFarmUserInfosMap: {}, myMoleIdsWithStaked: [], stakedMoleIds: [] }

  const { tokenIds: molesIds } = await queryClient.fetchQuery(queryKeyTokensOfUser(account), () =>
    queryTokensOfUser(queryClient, vvsMinerMolecontract, account),
  )
  const boostedFarmUserInfosMap = boostedFarmIdToUserInfosMap(
    await queryClient.fetchQuery(queryKeyBoostedFarmUserInfos(account), () =>
      queryBoostedFarmUserInfos(queryClient, vvsFarmBoostContract, account),
    ),
  )

  const stakedMoleIds = boostedFarmUserInfosMap
    ? Object.entries(boostedFarmUserInfosMap).flatMap(([, farmUserInfo]) =>
        farmUserInfo[2].map((nft) => nft[1].toString()),
      )
    : []
  const myMoleIdsWithStaked = sortedUniq([...molesIds, ...stakedMoleIds].sort((a, b) => parseInt(a) - parseInt(b)))

  return { boostedFarmUserInfosMap, myMoleIdsWithStaked, stakedMoleIds }
}
export const useMyMoleIdsWithStaked = (queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>) => {
  const { account } = useActiveWeb3React()
  const queryClient = useQueryClient()
  const vvsMinerMoleContract = useVvsMinerMoleContract()
  const vvsFarmBoostContract = useVvsFarmBoostContract()

  return useQuery(
    queryKeyMyMoleIdsWithStaked(account),
    () => queryMyMoleIdsWithStaked(queryClient, vvsMinerMoleContract, vvsFarmBoostContract, account),
    {
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    },
  )
}
